"use client";
import { UsersDetailsSkeletons } from "../../components/skeletons/users-skeletons";
import { useUserDetail } from "../../../hooks/api/user-table/hooks";
import { Breadcrumb, Button, Image } from "antd";
import { ArrowLeft } from "lucide-react";
import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import TwoColumnGrid from "../../components/reuseComp/TwoColumnGrid";
import DetailsSectionCard from "../../components/reuseComp/DetailsSectionCard";
import { UpcomingConsultationsType } from "@src/types/api/app-user";
import React from "react";

const UserDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { data: userDetail, isLoading } = useUserDetail(id ?? "");

  const filling_summary = userDetail?.data?.filling_summary?.[0];

  return (
    <div className="w-full">
      {/* TOP HEADER */}
      <div className="mb-6">
        <div className="flex justify-start w-full">
          <Button
            className="flex text-who items-center gap-2 mb-2 cursor-pointer"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={20} className="text-primary" />
            <span className="text-primary font-semibold">Back</span>
          </Button>
        </div>

        <h1 className="text-lg sm:text-3xl font-semibold sm:mb-1">
          User Details
        </h1>

        <Breadcrumb
          separator=">"
          items={[
            { title: "Dashboard" },
            { title: "Users" },
            { title: "User Detail" },
          ]}
          className="text-xs sm:text-base"
        />
      </div>

      {/* GRID LAYOUT */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* LEFT */}
        <div className="space-y-6">
          {/* BASIC INFO */}
          {isLoading ? (
            <UsersDetailsSkeletons />
          ) : (
            <DetailsSectionCard title="Basic Info">
              <TwoColumnGrid
                label="User ID"
                value={userDetail?.data?.user_Id ?? ""}
              />

              <TwoColumnGrid
                label="Full Name"
                value={userDetail?.data?.name ?? ""}
              />
              <TwoColumnGrid
                label="Email"
                value={userDetail?.data?.email ?? "N/A"}
              />
              <TwoColumnGrid
                label="Phone"
                value={userDetail?.data?.phone ?? "N/A"}
              />

              <TwoColumnGrid
                label="Date Joined"
                value={moment(userDetail?.data?.createdAt).format("DD/MM/YYYY")}
              />

              <TwoColumnGrid
                label="National Insurance Number"
                value={userDetail?.data?.nin_number ?? "N/A"}
              />
              <TwoColumnGrid
                label="Address"
                value={userDetail?.data?.address ?? "N/A"}
              />

              <TwoColumnGrid
                label="CDD Status"
                value={userDetail?.data?.status ? "Yes" : "No"}
              />
            </DetailsSectionCard>
          )}

          {/* PAYMENT SUMMARY */}
          {isLoading ? (
            <UsersDetailsSkeletons />
          ) : (
            <DetailsSectionCard
              title="Payment Summary"
              link="View Payment History"
            >
              {/* <TwoColumnGrid label="Last Transaction" value="7 Nov 2024" />
              <TwoColumnGrid label="Report Name" value="XYZ" /> */}
            </DetailsSectionCard>
          )}

          {/* CONSULTATIONS */}
          {isLoading ? (
            <UsersDetailsSkeletons />
          ) : (
            <DetailsSectionCard
              contentClassName="grid grid-cols-1 lg:grid-cols-3 gap-4"
              title="Consultations"
              // link="View Consultation"
            >
              <UpcommingsConsultationList
                data={
                  userDetail?.data
                    ?.upcomingConsultations as UpcomingConsultationsType[]
                }
              />
            </DetailsSectionCard>
          )}
        </div>

        {/* RIGHT */}
        <div className="space-y-6 col-span-1 lg:col-span-1">
          {/* FILING SUMMARY */}
          {isLoading ? (
            <UsersDetailsSkeletons />
          ) : (
            <DetailsSectionCard title="Filing Summary">
              {filling_summary ? (
                <>
                  <TwoColumnGrid
                    label="Current Filing"
                    value={
                      filling_summary
                        ? `${filling_summary?.filling_type}-${filling_summary?.filling_year}`
                        : ""
                    }
                  />
                  <TwoColumnGrid
                    label="Status"
                    value={`${filling_summary?.progress}% Complete`}
                  />
                  <TwoColumnGrid
                    label="Last Updated"
                    value={
                      filling_summary?.updatedAt
                        ? moment(filling_summary.updatedAt).fromNow()
                        : ""
                    }
                  />
                </>
              ) : (
                "No Data Found"
              )}
            </DetailsSectionCard>
          )}

          {/* PROFILE IMAGE */}
          {isLoading ? (
            <UsersDetailsSkeletons />
          ) : (
            <DetailsSectionCard title="Profile Image">
              <Image
                src={userDetail?.data?.avatar ?? ""}
                className="w-full max-w-sm rounded-xl object-cover mx-auto"
                alt="profile"
              />
            </DetailsSectionCard>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetails;

function UpcommingsConsultationList({
  data = [],
}: {
  data: UpcomingConsultationsType[];
}) {
  const navigate = useNavigate();
  return (
    <>
      {data?.map((item, index) => (
        <React.Fragment key={index}>
          <TwoColumnGrid
            label="Date/Time"
            value={`${
              moment(item.date).format("DD/MM/YYYY") +
              " ," +
              moment(item.start_time).format("hh:mm A")
            }`}
          />

          <TwoColumnGrid label="Status" value={item.status ?? ""} />
          <div>
            <Button
              className="bg-primary"
              onClick={() => navigate(`/consultation/details/${item._id}`)}
            >
              View
            </Button>
          </div>
        </React.Fragment>
      ))}
    </>
  );
}
